import { useState, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { CHART_ROLE_MAP, GENERIC_ROLES, computeSmartGuess } from './chartRoles';
import LineChartMappingPanel from './LineChartMappingPanel';
import BarMappingPanel from './BarMappingPanel';
import RadarMappingPanel from './RadarMappingPanel';
import ScatterMappingPanel from './ScatterMappingPanel';
import ComboMappingPanel from './ComboMappingPanel';

function ColumnMappingPanel({ columns, rows, selectedChartId, onConfirm, onBack, existingMapping }) {
  const { resolved: theme } = useTheme();
  const ui = theme.ui || {};

  const roles = selectedChartId ? (CHART_ROLE_MAP[selectedChartId] || GENERIC_ROLES) : GENERIC_ROLES;

  const smartGuess = useMemo(
    () => computeSmartGuess(columns, selectedChartId, rows),
    [columns, selectedChartId, rows]
  );
  const initialMapping = existingMapping && Object.keys(existingMapping).length > 0
    ? existingMapping
    : smartGuess;

  // mapping state: { role -> columnName }
  const [mapping, setMapping] = useState(initialMapping);

  if (selectedChartId === 'line-chart') {
    return (
      <LineChartMappingPanel
        columns={columns}
        rows={rows}
        initialMapping={initialMapping}
        onConfirm={onConfirm}
        onBack={onBack}
        ui={ui}
      />
    );
  }

  if (selectedChartId === 'grouped-bar' || selectedChartId === 'stacked-bar') {
    return (
      <BarMappingPanel
        columns={columns}
        rows={rows}
        initialMapping={initialMapping}
        chartId={selectedChartId}
        onConfirm={onConfirm}
        onBack={onBack}
        ui={ui}
      />
    );
  }

  if (selectedChartId === 'radar') {
    return (
      <RadarMappingPanel
        columns={columns}
        rows={rows}
        initialMapping={initialMapping}
        onConfirm={onConfirm}
        onBack={onBack}
        ui={ui}
      />
    );
  }

  if (selectedChartId === 'scatter-plot') {
    return (
      <ScatterMappingPanel
        columns={columns}
        rows={rows}
        initialMapping={initialMapping}
        onConfirm={onConfirm}
        onBack={onBack}
        ui={ui}
      />
    );
  }

  if (selectedChartId === 'combo') {
    return (
      <ComboMappingPanel
        columns={columns}
        rows={rows}
        initialMapping={initialMapping}
        onConfirm={onConfirm}
        onBack={onBack}
        ui={ui}
      />
    );
  }

  // Build reverse map: columnName -> role
  const columnToRole = {};
  for (const [role, colName] of Object.entries(mapping)) {
    columnToRole[colName] = role;
  }

  const handleRoleChange = (columnName, newRole) => {
    setMapping(prev => {
      const next = { ...prev };

      // Remove this column from any previous role
      for (const [role, col] of Object.entries(next)) {
        if (col === columnName) {
          delete next[role];
        }
      }

      // If another column had this role, unassign it
      if (newRole && newRole !== 'not-used') {
        delete next[newRole]; // remove previous column from this role
        next[newRole] = columnName;
      }

      return next;
    });
  };

  // Check if all required roles are assigned
  const requiredRoles = roles.filter(r => r.required);
  const missingRequired = requiredRoles.filter(r => !mapping[r.role]);

  const handleConfirm = () => {
    onConfirm(mapping);
  };

  // Available role options for a column's dropdown
  const roleOptions = [
    { value: 'not-used', label: 'Not used' },
    ...roles.map(r => ({ value: r.role, label: r.label + (r.required ? ' *' : '') })),
  ];

  return (
    <div>
      <p className="text-xs mb-3" style={{ color: ui.textSecondary }}>
        Tell us what each column represents. Required fields are marked with *.
      </p>

      <div
        className="border overflow-x-auto mb-4"
        style={{ borderColor: ui.border }}
      >
        <table className="w-full text-xs">
          <thead>
            <tr
              className="border-b"
              style={{ borderBottomColor: ui.border, backgroundColor: ui.bgSecondary }}
            >
              {columns.map((col) => (
                <th
                  key={col.name}
                  className="px-2 py-1 text-left font-medium border-r last:border-r-0"
                  style={{ borderRightColor: ui.borderSecondary, color: ui.text }}
                >
                  <div className="mb-1 truncate" title={col.name}>
                    {col.name}
                    <span className="ml-1" style={{ color: ui.textSecondary }}>
                      ({col.type === 'number' ? '#' : 'abc'})
                    </span>
                  </div>
                  <select
                    value={columnToRole[col.name] || 'not-used'}
                    onChange={(e) => handleRoleChange(col.name, e.target.value)}
                    className="w-full text-xs border py-0.5 px-1"
                    style={{ borderColor: ui.borderSecondary }}
                  >
                    {roleOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 5).map((row, i) => (
              <tr
                key={i}
                className="border-b last:border-b-0"
                style={{ borderBottomColor: ui.borderSecondary }}
              >
                {columns.map((col) => (
                  <td
                    key={col.name}
                    className="px-2 py-1 truncate max-w-[120px] border-r last:border-r-0"
                    style={{ borderRightColor: ui.borderSecondary, color: ui.text }}
                  >
                    {row[col.name] !== null && row[col.name] !== undefined
                      ? String(row[col.name])
                      : <span style={{ color: ui.textSecondary }}>—</span>
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mapping summary */}
      {missingRequired.length > 0 && (
        <p className="text-xs text-amber-600 border border-amber-300 bg-amber-50 p-2 mb-3">
          Missing required: {missingRequired.map(r => r.label).join(', ')}
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={onBack}
          className="flex-1 text-xs border py-2 ui-hover ui-border"
          style={{ borderColor: ui.border, color: ui.text }}
        >
          &larr; Back
        </button>
        <button
          onClick={handleConfirm}
          disabled={missingRequired.length > 0}
          className={`flex-1 text-xs border py-2 font-medium ui-border ${
            missingRequired.length > 0
              ? 'opacity-40 cursor-not-allowed'
              : 'ui-hover'
          }`}
          style={{ borderColor: ui.border, color: ui.text }}
        >
          Confirm &rarr;
        </button>
      </div>
    </div>
  );
}

export default ColumnMappingPanel;
